import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import { AddRounded } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";

import { useDialog } from "../../../DialogContext";
import {
  generateUniqueMatterName as defaultGenerateUniqueMatterName,
  pageScrollStyle,
} from "../../../../styles/styles";
import { getCases } from "../../../../redux/Apps/Lawyerapp/CaseSlice/CaseSlice";
import { NoDataOptionField } from "../../../../static/staticdata";
import noOption from "../../../../assets/no-option-found.webp";
import { getNestedError } from "./InputFields";

const MatterApiField = ({
  name,
  label = "Matter",
  value,
  onChange,
  errors,
  token,
  apiUrl,
  isRequired = false,
  renderNoOptions,
  AddMatterFunc,
  formatApiResponse,
  defaultValue = null,
  editData = null,
  MatterFormComponent,
  matterFormProps = {},
  createMatterDialogTitle = "Create Matter",
  createMatterDialogProps = {},
  noDataText = "Oops! Looks like there are no matters to display",
  addButtonText = "Add Matter",
  placeholder = "Select matter",
  returnFullData = false,
  disabled = false,
  searchKey = "name",
  multiple = false,
  freeSolo = false,
  generateUniqueMatterName:
    generateMatterLabel = defaultGenerateUniqueMatterName,
}) => {
  const dispatch = useDispatch();
  const { showDialog } = useDialog();
  const { teamMemberLawyerId } = useSelector((state) => state.user);

  const hasExternalValue = value !== undefined;
  const emptyValue = useMemo(() => (multiple ? [] : null), [multiple]);

  const [options, setOptions] = useState([]);
  const [internalValue, setInternalValue] = useState(() =>
    defaultValue ?? emptyValue,
  );
  const [nextUrl, setNextUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const isMountedRef = useRef(true);
  const abortControllerRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const isLoadingRef = useRef(false);
  const nextUrlRef = useRef(null);
  const searchQueryRef = useRef("");
  const latestSelectionRef = useRef(emptyValue);
  const handleScrollRef = useRef(null);
  const handleCreateMatterRef = useRef(null);
  const listboxMetaRef = useRef({
    addButtonText,
    hasCreateAction: false,
    isLoadingMore: false,
    nextUrl: null,
  });

  const isMatterObject = useCallback(
    (item) => Boolean(item && typeof item === "object" && !Array.isArray(item)),
    [],
  );

  const normalizeSearchValue = useCallback((input) => {
    if (input == null) return "";
    return String(input).trim().toLowerCase();
  }, []);

  const getMatterId = useCallback(
    (item) => {
      if (item == null || item === "") return null;
      return isMatterObject(item) ? item.id ?? null : item;
    },
    [isMatterObject],
  );

  const mergeOptions = useCallback((...collections) => {
    const merged = new Map();

    collections.flat().forEach((item) => {
      if (item == null || item === "") return;

      const key =
        typeof item === "string"
          ? `string-${item}`
          : item?.id !== undefined
            ? `matter-${item.id}`
            : `fallback-${String(item)}`;

      if (!merged.has(key)) {
        merged.set(key, item);
      }
    });

    return Array.from(merged.values());
  }, []);

  const formatMatterLabel = useCallback(
    (option) => {
      if (!option) return "";
      if (typeof option === "string") return option;

      const clientRef = String(option?.client_ref || "").trim();
      const caseName = String(option?.case_name || option?.name || "").trim();

      if (clientRef && caseName) {
        return `${clientRef} - ${caseName}`;
      }

      if (
        caseName &&
        typeof generateMatterLabel === "function" &&
        generateMatterLabel !== defaultGenerateUniqueMatterName &&
        option?.id != null
      ) {
        return generateMatterLabel(option.id, caseName);
      }

      return caseName || clientRef || "";
    },
    [generateMatterLabel],
  );

  const getOptionLabel = useCallback(
    (option) => formatMatterLabel(option),
    [formatMatterLabel],
  );

  const effectiveValue = hasExternalValue ? value : internalValue;

  const pinnedObjects = useMemo(() => {
    const pinned = [];

    const pushItem = (item) => {
      if (!item) return;

      if (Array.isArray(item)) {
        item.forEach(pushItem);
        return;
      }

      if (isMatterObject(item)) {
        pinned.push(item);
      }
    };

    pushItem(defaultValue);
    pushItem(editData);
    pushItem(effectiveValue);

    return mergeOptions(pinned);
  }, [defaultValue, editData, effectiveValue, isMatterObject, mergeOptions]);

  const resolveMatterOption = useCallback(
    (item) => {
      if (item == null || item === "") {
        return null;
      }

      if (typeof item === "string") {
        return freeSolo ? item : null;
      }

      const matterId = getMatterId(item);

      return (
        options.find((option) => option?.id === matterId) ||
        pinnedObjects.find((option) => option?.id === matterId) ||
        item
      );
    },
    [freeSolo, getMatterId, options, pinnedObjects],
  );

  // Derive the displayed selection from the raw value so controlled and
  // uncontrolled usage follow the same path.
  const resolvedSelection = useMemo(() => {
    if (multiple) {
      const rawItems = Array.isArray(effectiveValue)
        ? effectiveValue
        : effectiveValue
          ? [effectiveValue]
          : [];

      return mergeOptions(rawItems.map(resolveMatterOption).filter(Boolean));
    }

    return resolveMatterOption(effectiveValue);
  }, [effectiveValue, mergeOptions, multiple, resolveMatterOption]);

  const selectedOptionsArray = useMemo(() => {
    if (multiple) {
      return Array.isArray(resolvedSelection) ? resolvedSelection : [];
    }

    return resolvedSelection ? [resolvedSelection] : [];
  }, [multiple, resolvedSelection]);

  const selectedOptions = multiple
    ? selectedOptionsArray
    : selectedOptionsArray[0] || null;

  const mergedOptions = useMemo(
    () => mergeOptions(options, pinnedObjects, selectedOptionsArray),
    [mergeOptions, options, pinnedObjects, selectedOptionsArray],
  );

  const getMatterSearchRank = useCallback(
    (option, query) => {
      if (!option || !query || typeof option === "string") {
        return Number.MAX_SAFE_INTEGER;
      }

      const normalizedQuery = normalizeSearchValue(query);
      if (!normalizedQuery) return Number.MAX_SAFE_INTEGER;

      const caseName = normalizeSearchValue(option?.case_name || option?.name);
      const clientRef = normalizeSearchValue(option?.client_ref);
      const matterId = normalizeSearchValue(option?.id);
      const compositeLabel = normalizeSearchValue(getOptionLabel(option));

      if (
        [matterId, clientRef, caseName, compositeLabel].some(
          (value) => value && value === normalizedQuery,
        )
      ) {
        return 0;
      }

      if (
        [clientRef, caseName, matterId, compositeLabel].some(
          (value) => value && value.startsWith(normalizedQuery),
        )
      ) {
        return 1;
      }

      if (
        [clientRef, caseName, matterId, compositeLabel].some(
          (value) => value && value.includes(normalizedQuery),
        )
      ) {
        return 2;
      }

      return 3;
    },
    [getOptionLabel, normalizeSearchValue],
  );

  const displayedOptions = useMemo(() => {
    if (!searchQuery) {
      return mergedOptions;
    }

    const matchedOptions = mergedOptions.filter(
      (option) =>
        typeof option === "string" ||
        getMatterSearchRank(option, searchQuery) < 3,
    );

    return [...matchedOptions].sort((left, right) => {
      if (typeof left === "string" || typeof right === "string") {
        return normalizeSearchValue(getOptionLabel(left)).localeCompare(
          normalizeSearchValue(getOptionLabel(right)),
        );
      }

      const leftRank = getMatterSearchRank(left, searchQuery);
      const rightRank = getMatterSearchRank(right, searchQuery);

      if (leftRank !== rightRank) {
        return leftRank - rightRank;
      }

      return normalizeSearchValue(getOptionLabel(left)).localeCompare(
        normalizeSearchValue(getOptionLabel(right)),
      );
    });
  }, [
    getMatterSearchRank,
    getOptionLabel,
    mergedOptions,
    normalizeSearchValue,
    searchQuery,
  ]);

  const setLoadingState = useCallback((loading, loadMore = false) => {
    isLoadingRef.current = loading;

    if (!isMountedRef.current) return;

    if (loadMore) {
      setIsLoadingMore(loading);
      return;
    }

    setIsLoading(loading);
  }, []);

  const getPageFromUrl = useCallback((url) => {
    if (!url) return 1;

    try {
      const parsedUrl = new URL(url);
      return parseInt(parsedUrl.searchParams.get("page"), 10) || 1;
    } catch {
      return 1;
    }
  }, []);

  const fetchMatters = useCallback(
    async (query = "", { loadMore = false } = {}) => {
      if (loadMore && (!nextUrlRef.current || isLoadingRef.current)) {
        return;
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();
      setLoadingState(true, loadMore);

      try {
        const result = await dispatch(
          getCases({
            url: apiUrl,
            token,
            page: loadMore ? getPageFromUrl(nextUrlRef.current) : 1,
            search: query,
            searchKey: query ? "search" : searchKey,
            teamMemberLawyerId,
            signal: abortControllerRef.current.signal,
          }),
        ).unwrap();

        if (!isMountedRef.current) return;

        const results = result?.results || [];
        const formatted = formatApiResponse
          ? formatApiResponse({ res: results, api: apiUrl })
          : results;
        const nextOptions = Array.isArray(formatted) ? formatted : [];

        setOptions((prev) =>
          loadMore
            ? mergeOptions(prev, nextOptions, pinnedObjects, latestSelectionRef.current)
            : mergeOptions(nextOptions, pinnedObjects, latestSelectionRef.current),
        );

        const nextPageUrl = result?.pagination?.next || result?.next || null;
        setNextUrl(nextPageUrl);
        nextUrlRef.current = nextPageUrl;
      } catch (error) {
        if (error?.name === "AbortError" || error?.code === "ERR_CANCELED") {
          return;
        }

        if (!isMountedRef.current) return;

        console.error("Fetch matters error:", error);
        setOptions(mergeOptions(pinnedObjects, latestSelectionRef.current));
        setNextUrl(null);
        nextUrlRef.current = null;
      } finally {
        if (isMountedRef.current) {
          setLoadingState(false, loadMore);
        }
      }
    },
    [
      apiUrl,
      dispatch,
      formatApiResponse,
      getPageFromUrl,
      mergeOptions,
      pinnedObjects,
      searchKey,
      setLoadingState,
      teamMemberLawyerId,
      token,
    ],
  );

  const formatOutputValue = useCallback(
    (selection) => {
      if (returnFullData) {
        return selection;
      }

      if (multiple) {
        return Array.isArray(selection)
          ? selection.map((item) => getMatterId(item)).filter(Boolean)
          : [];
      }

      return getMatterId(selection) ?? null;
    },
    [getMatterId, multiple, returnFullData],
  );

  const emitChange = useCallback(
    (selection) => {
      if (!onChange) return;
      onChange(formatOutputValue(selection));
    },
    [formatOutputValue, onChange],
  );

  const handleInputChange = useCallback((_, nextInput, reason) => {
    if (reason === "input") {
      setSearchQuery(nextInput || "");
      return;
    }

    if (reason === "clear" || reason === "reset") {
      setSearchQuery("");
    }
  }, []);

  const handleChange = useCallback(
    (_, newValue) => {
      const normalizedSelection = multiple
        ? mergeOptions(Array.isArray(newValue) ? newValue : [])
        : newValue || null;

      latestSelectionRef.current = normalizedSelection;

      if (!hasExternalValue) {
        setInternalValue(formatOutputValue(normalizedSelection));
      }

      setOptions((prev) =>
        mergeOptions(
          prev,
          Array.isArray(normalizedSelection)
            ? normalizedSelection
            : [normalizedSelection].filter(Boolean),
        ),
      );

      setSearchQuery("");
      emitChange(normalizedSelection);
    },
    [
      emitChange,
      formatOutputValue,
      hasExternalValue,
      mergeOptions,
      multiple,
    ],
  );

  const handleMatterCreated = useCallback(
    (newMatter) => {
      if (!isMatterObject(newMatter) || !newMatter.id) return;

      const nextSelection = multiple
        ? mergeOptions(selectedOptionsArray, [newMatter])
        : newMatter;

      latestSelectionRef.current = nextSelection;

      if (!hasExternalValue) {
        setInternalValue(formatOutputValue(nextSelection));
      }

      setOptions((prev) =>
        mergeOptions(prev, [newMatter], selectedOptionsArray, pinnedObjects),
      );
      setSearchQuery("");
      emitChange(nextSelection);
    },
    [
      emitChange,
      formatOutputValue,
      hasExternalValue,
      isMatterObject,
      mergeOptions,
      multiple,
      pinnedObjects,
      selectedOptionsArray,
    ],
  );

  const handleCreateMatter = useCallback(() => {
    if (MatterFormComponent) {
      showDialog(
        <MatterFormComponent
          {...matterFormProps}
          onSubmitSuccess={handleMatterCreated}
        />,
        {
          dialogTitle: createMatterDialogTitle,
          ...createMatterDialogProps,
        },
      );
      return;
    }

    if (AddMatterFunc) {
      AddMatterFunc({ onCreated: handleMatterCreated, handleMatterCreated });
    }
  }, [
    AddMatterFunc,
    MatterFormComponent,
    createMatterDialogProps,
    createMatterDialogTitle,
    handleMatterCreated,
    matterFormProps,
    showDialog,
  ]);

  const hasCreateAction = Boolean(AddMatterFunc || MatterFormComponent);

  const handleScroll = useCallback(
    (event) => {
      const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;

      if (
        !isLoadingRef.current &&
        nextUrlRef.current &&
        scrollTop + clientHeight >= scrollHeight - 50
      ) {
        fetchMatters(searchQueryRef.current, { loadMore: true });
      }
    },
    [fetchMatters],
  );

  handleScrollRef.current = handleScroll;
  handleCreateMatterRef.current = handleCreateMatter;
  listboxMetaRef.current = {
    addButtonText,
    hasCreateAction,
    isLoadingMore,
    nextUrl,
  };

  // Keep the dropdown listbox identity stable so loading/footer changes do not
  // remount the popup and cause flicker while selecting multiple items.
  const ListboxComponent = useMemo(
    () =>
      forwardRef(function MatterListbox(props, ref) {
        const { children, ...rest } = props;
        const meta = listboxMetaRef.current;

        return (
          <Box ref={ref} sx={{ position: "relative" }}>
            <Box
              component="ul"
              {...rest}
              sx={{
                ...pageScrollStyle,
                maxHeight: "250px",
                overflowY: "auto",
                listStyle: "none",
                m: 0,
                p: 0,
              }}
              onScroll={(event) => handleScrollRef.current?.(event)}
            >
              {children}
              {meta.isLoadingMore && meta.nextUrl && (
                <Box sx={{ p: 1 }}>
                  {[1, 2, 3].map((item) => (
                    <Box
                      key={item}
                      sx={{ display: "flex", alignItems: "center", gap: 1, p: 1 }}
                    >
                      <Skeleton variant="circular" width={8} height={8} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="80%" height={20} />
                        <Skeleton variant="text" width="40%" height={16} />
                      </Box>
                      <Skeleton variant="text" width="20%" height={16} />
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {meta.hasCreateAction && (
              <Box
                sx={{
                  position: "sticky",
                  bottom: 0,
                  borderTop: "1px solid #e5e5e5",
                  bgcolor: "white",
                  p: 1,
                  display: "flex",
                  justifyContent: "flex-end",
                }}
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
              >
                <Button
                  variant="text"
                  size="small"
                  color="primary"
                  endIcon={<AddRounded />}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    handleCreateMatterRef.current?.();
                  }}
                >
                  {meta.addButtonText}
                </Button>
              </Box>
            )}
          </Box>
        );
      }),
    [],
  );

  useEffect(() => {
    latestSelectionRef.current = selectedOptions;
  }, [selectedOptions]);

  useEffect(() => {
    searchQueryRef.current = searchQuery;
  }, [searchQuery]);

  useEffect(() => {
    nextUrlRef.current = nextUrl;
  }, [nextUrl]);

  useEffect(() => {
    if (!hasExternalValue) {
      setInternalValue(defaultValue ?? emptyValue);
    }
  }, [defaultValue, emptyValue, hasExternalValue]);

  // Keep selected/edit/default items visible even when they are not in the
  // latest fetched page.
  useEffect(() => {
    if (pinnedObjects.length === 0 && selectedOptionsArray.length === 0) {
      return;
    }

    setOptions((prev) => mergeOptions(prev, pinnedObjects, selectedOptionsArray));
  }, [mergeOptions, pinnedObjects, selectedOptionsArray]);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    searchTimeoutRef.current = setTimeout(() => {
      fetchMatters(searchQueryRef.current);
    }, 350);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [fetchMatters, searchQuery]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const statusColors = {
    open: "#34D399",
    pending: "#FBBF24",
    closed: "#F97316",
    Open: "#34D399",
    Pending: "#FBBF24",
    Closed: "#F97316",
  };

  const fieldError = getNestedError(errors, name);

  return (
    <FormControl fullWidth size="small">
      <Autocomplete
        fullWidth
        disabled={disabled}
        multiple={multiple}
        disableCloseOnSelect={multiple}
        freeSolo={freeSolo}
        options={displayedOptions}
        value={selectedOptions}
        inputValue={multiple ? searchQuery : undefined}
        onInputChange={handleInputChange}
        onChange={handleChange}
        loading={isLoading && !isLoadingMore}
        getOptionLabel={getOptionLabel}
        filterSelectedOptions={multiple}
        clearOnBlur={false}
        isOptionEqualToValue={(option, selectedValue) => {
          if (typeof option === "string" || typeof selectedValue === "string") {
            return option === selectedValue;
          }

          return option?.id === selectedValue?.id;
        }}
        filterOptions={(items) => items}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={
                typeof option === "string"
                  ? `tag-${option}-${index}`
                  : option?.id || index
              }
              label={getOptionLabel(option)}
              size="small"
            />
          ))
        }
        noOptionsText={
          renderNoOptions || (
            <NoDataOptionField
              imgSrc={noOption}
              text={noDataText}
              btnText={hasCreateAction ? addButtonText : undefined}
              handleClickNoData={
                hasCreateAction ? handleCreateMatter : undefined
              }
            />
          )
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={
              <span>
                {label}
                {isRequired && <span style={{ color: "red" }}> *</span>}
              </span>
            }
            placeholder={placeholder}
            size="small"
            error={Boolean(fieldError)}
            helperText={fieldError?.message}
            inputProps={{ ...params.inputProps, name }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading && !isLoadingMore && (
                    <CircularProgress size={20} color="primary" />
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        ListboxComponent={ListboxComponent}
        PopperProps={{
          container: document.body,
          sx: { zIndex: 14000 },
        }}
        renderOption={(props, option) => {
          if (typeof option === "string") {
            return (
              <li {...props}>
                <Typography variant="body2">{option}</Typography>
              </li>
            );
          }

          const status = option?.case_status || option?.status;
          const statusColor = statusColors[status] || "#9CA3AF";

          return (
            <li
              {...props}
              key={option?.id || getOptionLabel(option)}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "8px 12px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    sx={{
                      fontSize: "0.875rem",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      mb: 0.5,
                    }}
                  >
                    {formatMatterLabel(option)}
                  </Typography>

                  {status && (
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: statusColor,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                      >
                        {status}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </li>
          );
        }}
      />
    </FormControl>
  );
};

export default MatterApiField;
